/* eslint-disable @typescript-eslint/no-explicit-any */
import { TableName } from '../../constants/table';
import {
  ICompanyMaster,
  CompanyMasterWithOCR,
} from '../../models/companyMaster';
import {
  getByPartitionKey,
  getFilteredEntities,
} from '../services/api-service';
import { useQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

const companyMasterTableName = TableName.CompanyMaster;
const companyMappingTableName = TableName.CompanyMapping;

const getAllCompanyMasters = async (tenantId: string) => {
  const companyMappings = await getByPartitionKey(
    companyMappingTableName,
    tenantId,
  );
  const companyMasters = await getByPartitionKey(
    companyMasterTableName,
    tenantId,
  );

  // Sort mappings by Timestamp ascending so the oldest (original) mapping
  // is kept by uniqBy, preserving the original CompanyOCR for each company.
  const sortedMappings = [...companyMappings].sort(
    (a: any, b: any) =>
      new Date(a.Timestamp ?? 0).getTime() - new Date(b.Timestamp ?? 0).getTime(),
  );

  const data = sortedMappings.map((item: any) => ({
    ...item,
    RowKey: item.CompanyId,
    Active: companyMasters.find((e: any) => e.RowKey === item.CompanyId)?.Active,
    IsSystemGenerated: companyMasters.find((e: any) => e.RowKey === item.CompanyId)
      ?.IsSystemGenerated,
    CompanyName: companyMasters.find((e: any) => e.RowKey === item.CompanyId)
      ?.CompanyName,
    CompanyGst: companyMasters.find((e: any) => e.RowKey === item.CompanyId)
      ?.CompanyGst,
    ExportFormat: companyMasters.find((e: any) => e.RowKey === item.CompanyId)
      ?.ExportFormat,
  }));

  // Deduplicate by RowKey (CompanyId) — since mappings are sorted oldest-first,
  // the original CompanyOCR is preserved for each company.
  const uniqueData = uniqBy(data, 'RowKey').map((item, index) => ({
    ...item,
    id: index,
  }));

  return uniqueData as CompanyMasterWithOCR[];
};

export const getActiveCompanyCount = async (tenantId: string) => {
  const companyMasterList: ICompanyMaster[] = await getFilteredEntities(
    companyMasterTableName,
    tenantId,
  );

  return uniqBy(companyMasterList, e => e.CompanyName).filter(
    (item: ICompanyMaster) => item.Active,
  ).length;
};

export const useGetAllCompanyMasters = (tenantId: string) =>
  useQuery<CompanyMasterWithOCR[]>({
    // Update useQuery type
    queryKey: ['getAllCompanyMaster', tenantId],
    queryFn: async () => getAllCompanyMasters(tenantId),
  });
